// updatedRecipeSummary.js
import _ from 'lodash';

export function recipeSummary(recipeValues, oils) {
  const oilsLookup = _.keyBy(oils, 'id');

  let totalOilWeight;
  let totalWaterWeight;
  let totalLye;
  let totalNaoh = 0;
  let totalKoh = 0;
  let citricAdjustLye = 0;
  let citricAdjustKoh = 0;
  let citricAdjustNaoh = 0;
  let totalSuperfat = 0;
  let lyeConcentration;
  let waterLyeRatio;
  let fragranceWeight;
  let breakdowns;
  let properties;
  let saturations;

  const citricAdjustPercentToValue = (recipeValues.citricAdjustPercent / 100);
  const isPercent = recipeValues.uom === 'percent';

  const totalWeight = isPercent
    ? recipeValues.totalsIncludeWater
      ? recipeValues.totalWeight / (1 + recipeValues.waterRatio / 100)
      : recipeValues.totalWeight
    : _.sumBy(recipeValues.oils, o => o.weight);

  totalOilWeight = totalWeight;
  if (!recipeValues.superfatAfter) {
    totalSuperfat = totalOilWeight * (recipeValues.superFat / 100);
  }

  const recipeWeightPairs = recipeValues.oils.map(o => ({ weightOrRatio: o.weight, oilId: o.id }));

  const lyeWeightForOilId = (lye, weightRatio, oilId) => {
    const oil = oilsLookup[oilId];
    if (!oil) return 0;
    let oilWeight = isPercent ? totalWeight * (weightRatio / 100) : weightRatio;
    const grams = oilWeight;
    const sap = sapForSoapType(lye, oil);
    let lyeGrams = grams * sap;
    if (!recipeValues.superfatAfter) {
      lyeGrams -= (recipeValues.superFat / 100) * lyeGrams;
    }
    return lyeGrams;
  };

  const sapForSoapType = (lye, oil) => {
    const purities = {
      naoh: (recipeValues.naohPurity || 100) / 100,
      koh: (recipeValues.kohPurity || 90) / 100,
    };
    return lye === 'koh' ? oil.sap * 1.403 / purities.koh : oil.sap / purities.naoh;
  };

  if (recipeValues.soapType === 'hybrid' || recipeValues.soapType === 'mixed') {
    totalNaoh = _.sumBy(recipeWeightPairs, pair => lyeWeightForOilId('naoh', pair.weightOrRatio, pair.oilId));
    totalKoh = _.sumBy(recipeWeightPairs, pair => lyeWeightForOilId('koh', pair.weightOrRatio, pair.oilId));

    if (recipeValues.enableCitricAdjust) {
      const kohRatioAdjust = recipeValues.ratioKoh / 100;
      const naohRatioAdjust = recipeValues.ratioNaoh / 100;
      citricAdjustKoh = totalOilWeight * kohRatioAdjust * citricAdjustPercentToValue * citricAdjustForSoapType('koh');
      citricAdjustNaoh = totalOilWeight * naohRatioAdjust * citricAdjustPercentToValue * citricAdjustForSoapType('naoh');
      totalKoh += citricAdjustKoh;
      totalNaoh += citricAdjustNaoh;
    }

    totalLye = totalNaoh + totalKoh;
  } else {
    totalLye = _.sumBy(recipeWeightPairs, pair => lyeWeightForOilId(recipeValues.soapType, pair.weightOrRatio, pair.oilId));

    if (recipeValues.enableCitricAdjust) {
      citricAdjustLye = totalOilWeight * citricAdjustPercentToValue * citricAdjustForSoapType(recipeValues.soapType);
      totalLye += citricAdjustLye;
    }

    if (recipeValues.soapType === 'naoh') totalNaoh = totalLye;
    else if (recipeValues.soapType === 'koh') totalKoh = totalLye;
  }

  // Fragrance
  fragranceWeight = recipeValues.fragranceType === 'ratio'
    ? totalOilWeight * (recipeValues.fragrance / 100)
    : totalOilWeight * (recipeValues.fragrancePpo / (['g', 'gram'].includes(recipeValues.uom) ? 1000 : 16));

  // Water calculation
  if (recipeValues.lyeCalcType === 'concentration') {
    const concentration = (recipeValues.recipeLyeConcentration || 100) / 100;
    totalWaterWeight = (totalLye / concentration) - totalLye;
    lyeConcentration = 100 * concentration;
  } else if (recipeValues.lyeCalcType === 'ratio') {
    totalWaterWeight = totalLye * recipeValues.lyeWaterLyeRatio;
    lyeConcentration = 100 * (totalLye / (totalWaterWeight + totalLye));
  } else {
    totalWaterWeight = totalOilWeight * (recipeValues.waterRatio / 100);
    lyeConcentration = 100 * (totalLye / (totalWaterWeight + totalLye));
  }

  totalWaterWeight -= (recipeValues.waterDiscount / 100) * totalWaterWeight;
  waterLyeRatio = totalWaterWeight / totalLye;

  const superfat = recipeValues.superFat;

  const totalBatchWeight = totalOilWeight + totalWaterWeight + totalLye + fragranceWeight + totalSuperfat;

  breakdowns = recipeOilFatBreakdowns();
  properties = recipeOilProperties();
  saturations = recipeOilSaturations();

  return {
    totals: {
      totalOilWeight,
      totalWaterWeight,
      totalNaoh,
      totalKoh,
      totalLye,
      totalSuperfat,
      superfat,
      totalBatchWeight,
      fragranceWeight,
      lyeConcentration,
      waterLyeRatio,
      citricAdjustKoh,
      citricAdjustNaoh,
      citricAdjustLye
    },
    display: {
      recipeOilsWeightsRatios: buildRecipeRatios()
    },
    breakdowns,
    properties,
    saturations
  };

  function citricAdjustForSoapType(type) {
    return type === 'naoh' ? 0.624 : 0.715;
  }

  function buildRecipeRatios() {
    return recipeWeightPairs.map(({ weightOrRatio, oilId }) => {
      const oil = oilsLookup[oilId];
      const ratio = isPercent ? weightOrRatio / 100 : weightOrRatio / totalOilWeight;
      const weight = isPercent ? totalOilWeight * ratio : weightOrRatio;
      return { oil, weight, ratio };
    });
  }

  function recipeOilFatBreakdowns() {
    return _.reduce(recipeWeightPairs, (result, { weightOrRatio, oilId }) => {
      const oil = oilsLookup[oilId];
      const ratio = isPercent ? weightOrRatio / 100 : weightOrRatio / totalOilWeight;
      _.each(oil.breakdown, (v, k) => {
        result[k] = (result[k] || 0) + v * ratio;
      });
      return result;
    }, {});
  }

  function recipeOilProperties() {
    return _.reduce(recipeWeightPairs, (result, { weightOrRatio, oilId }) => {
      const oil = oilsLookup[oilId];
      const ratio = isPercent ? weightOrRatio / 100 : weightOrRatio / totalOilWeight;
      _.each(oil.properties || {}, (v, k) => {
        result[k] = (result[k] || 0) + v * ratio;
      });
      result.iodine = (result.iodine || 0) + (oil.iodine || 0) * ratio;
      result.ins = (result.ins || 0) + (oil.ins || 0) * ratio;
      return result;
    }, {});
  }

  function recipeOilSaturations() {
    return _.reduce(recipeWeightPairs, (result, { weightOrRatio, oilId }) => {
      const oil = oilsLookup[oilId];
      const ratio = isPercent ? weightOrRatio / 100 : weightOrRatio / totalOilWeight;
      const saturated = _.sum(["caprylic", "capric", "lauric", "myristic", "palmitic", "stearic"].map(k => oil.breakdown[k] || 0));
      result.saturated += saturated * ratio;
      return result;
    }, {
      saturated: 0,
      get unsaturated() { return 100 - this.saturated; }
    });
  }
}
